// Control.js
import React, { useEffect, useState } from 'react';
import axiosInstance from '../../axiosConfig';
import GlassPaper from '../GlassPaper';
import { useAdminStrategies } from '../../hooks/useAdminStrategies';
import { strategyModelLabel } from '../../utils/strategyAccountModel';
import './Control.css';

const Control = () => {
    const { legacyStrategies, loading: strategiesLoading } = useAdminStrategies();
    const [accounts, setAccounts] = useState([]);
    const [selectedStrategy, setSelectedStrategy] = useState('');
    const [selectedAccount, setSelectedAccount] = useState('');
    const [interruptType, setInterruptType] = useState('squareoff');
    const [strategyDesc, setStrategyDesc] = useState('');
    const [accountName, setAccountName] = useState('');
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [confirmChecked, setConfirmChecked] = useState(false);
    const [responseMessage, setResponseMessage] = useState('');
    const [responseColor, setResponseColor] = useState('');

    useEffect(() => {
        const fetchAccounts = async () => {
            try {
                const response = await axiosInstance.get('/api/accounts');
                setAccounts(response.data);
            } catch (error) {
                console.error('Error fetching accounts:', error);
            }
        };

        fetchAccounts();
    }, []);

    const handleStrategyChange = (e) => {
        const selected = e.target.value;
        setSelectedStrategy(selected);
        const strategy = legacyStrategies.find((strategy) => strategy.strategy_name === selected);
        setStrategyDesc(strategy ? strategy.strategy_desc : '');
        setSelectedAccount(''); // Reset account and underlying selections
        setInterruptType('squareoff');
    };

    const handleAccountChange = (e) => {
        const selected = e.target.value;
        setSelectedAccount(selected);
        const account = accounts.find(account => account.app_name === selected);
        setAccountName(account ? account.account_name : '');
    };

    const handleInterruptTypeChange = (e) => {
        setInterruptType(e.target.value);
    };

    const handleCreateInterrupt = () => {
        setShowConfirmDialog(true);
    };

    const handleCancel = () => {
        setShowConfirmDialog(false);
        setSelectedStrategy('');
        setSelectedAccount('');
        setInterruptType('squareoff');
        setStrategyDesc('');
        setAccountName('');
    };

    const handleProceed = async () => {
        if (!confirmChecked) return;

        try {
            const response = await axiosInstance.post('/api/createInterrupt', null, {
                params: {
                    app_name: selectedAccount,
                    strategy_name: selectedStrategy,
                    interrupt_action: interruptType
                }
            });
            setResponseMessage(response.data.message);
            setResponseColor('green');
        } catch (error) {
            setResponseMessage('Error creating interrupt');
            setResponseColor('red');
        }

        setShowConfirmDialog(false);
        setConfirmChecked(false);
        setSelectedStrategy('');
        setSelectedAccount('');
        setInterruptType('squareoff');
        setStrategyDesc('');
        setAccountName('');
    };

    return (
        <GlassPaper className="control-shell" elevation={0} square={false}>
            <h2 className="dashboard-title">Interrupt</h2>
            <p className="description-text">
                Legacy algoverse strategies only (classification model). quant_platform strategies
                use qCockpit kill switch / account halt instead of ca_interrupts.
            </p>
            <div className="form-group mt-3">
                <label htmlFor="strategyAppNameSelect">Select Strategy</label>
                <select 
                    id="strategyAppNameSelect" 
                    className="form-control" 
                    value={selectedStrategy} 
                    onChange={handleStrategyChange}
                    disabled={strategiesLoading}
                >
                    <option value="">Select a legacy strategy</option>
                    {legacyStrategies.map((strategy, index) => (
                        <option key={index} value={strategy.strategy_name}>
                            {strategy.strategy_name}
                        </option>
                    ))}
                </select>
                {strategyDesc && <p className="description-text">{strategyDesc}</p>}
                {selectedStrategy && (
                    <p className="description-text">
                        {strategyModelLabel(legacyStrategies.find((s) => s.strategy_name === selectedStrategy))}
                    </p>
                )}
            </div>

            <div className="form-group mt-3">
                <label htmlFor="accountSelect">Select Account</label>
                <select 
                    id="accountSelect" 
                    className="form-control" 
                    value={selectedAccount} 
                    onChange={handleAccountChange}
                    disabled={!selectedStrategy} // Enable only if a strategy is selected
                >
                    <option value="">Select an account</option>
                    {accounts.map((account, index) => (
                        <option key={index} value={account.app_name}>
                            {account.app_name}
                        </option>
                    ))}
                </select>
                {accountName && <p className="description-text">{accountName}</p>}
            </div>

            <div className="form-group mt-3">
                <label htmlFor="interruptTypeSelect">Interrupt Type</label>
                <select
                    id="interruptTypeSelect"
                    className="form-control"
                    value={interruptType}
                    onChange={handleInterruptTypeChange}
                    disabled={!selectedAccount}
                >
                    <option value="squareoff">squareoff</option>
                    <option value="start">start</option>
                    <option value="cancel">cancel</option>
                </select>
            </div>

            <button 
                className="btn btn-sm btn-primary mt-3" 
                onClick={handleCreateInterrupt}
                disabled={!selectedStrategy || !selectedAccount}
            >
                Create Interrupt
            </button>

            {showConfirmDialog && (
                <GlassPaper className="confirm-dialog" elevation={0} square={false}>
                    <p>You are about to create an interrupt for <b>Strategy</b> - <i><u>{selectedStrategy}</u></i> on <b>Account</b> - <i><u>{selectedAccount}</u></i> with <b>Action</b> - <i><u>{interruptType}</u></i></p>
                    <div>
                        <input 
                            type="checkbox" 
                            id="confirmCheckbox" 
                            checked={confirmChecked} 
                            onChange={() => setConfirmChecked(!confirmChecked)} 
                        />
                        <label htmlFor="confirmCheckbox">I know what I am doing</label>
                    </div>
                    <div className="dialog-buttons">
                        <button className="btn btn-secondary btn-sm" onClick={handleCancel}>Cancel</button>
                        <button 
                            className="btn btn-primary btn-sm" 
                            onClick={handleProceed} 
                            disabled={!confirmChecked}
                        >
                            Proceed
                        </button>
                    </div>
                </GlassPaper>
            )}

            {responseMessage && (
                <div className="response-message" style={{ color: responseColor }}>
                    {responseMessage}
                    <button 
                        className="btn btn-secondary btn-sm mt-2" 
                        onClick={() => setResponseMessage('')}
                    >
                        Ok
                    </button>
                </div>
            )}
        </GlassPaper>
    );
};

export default Control;
